import type { ChartConfig } from '@lms/ui/chart';
import { brandPalette } from './palette';

/**
 * Neutral fallback palette, used when no brand is supplied. Slot order keeps
 * adjacent series distinguishable under colour-blindness; past eight series
 * the tail folds into "Other" rather than generating more hues.
 */
export const SERIES_COLORS = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
] as const;

/** Neutral used for the folded "Other" series. */
export const OTHER_COLOR = '#a8a6a0';
export const OTHER_KEY = 'other';
/**
 * Comparison bars (e.g. the same period last year): a light neutral,
 * distinct from the darker "Other" grey, so it reads as context.
 */
export const PREVIOUS_COLOR = '#d6d4ce';

export interface Series {
  /** Stable, CSS-safe key used as the Recharts dataKey. */
  key: string;
  /** Human-readable name, shown in legends and tooltips. */
  name: string;
  color: string;
}

export interface SeriesSet {
  /** Series in stack/legend order: largest first, "Other" last. */
  series: Series[];
  /** Maps a raw dimension value to its series key (folded names → Other). */
  keyOf: (name: string) => string;
  /** Looks up a series by raw dimension value. */
  get: (name: string) => Series;
  /** shadcn chart config, for ChartContainer. */
  config: ChartConfig;
}

export interface BuildSeriesOptions {
  /** Most series to show before folding the smallest into "Other". */
  max?: number;
  otherLabel?: string;
  /** Derive the colours from these brand colours (see `brandPalette`). */
  brand?: { primary: string; secondary?: string };
}

/**
 * Builds the series for one dimension (curriculum, location, status, …) from
 * the raw rows. Build it once per page and pass the same set to every chart
 * that shows that dimension, so an entity keeps its colour across charts.
 *
 * Series are ordered by total, largest first, and take palette slots in that
 * order; with a brand, the largest series wears the brand's primary.
 */
export function buildSeries<R>(
  rows: readonly R[],
  name: (row: R) => string | null | undefined,
  value: (row: R) => number,
  {
    max = SERIES_COLORS.length,
    otherLabel = 'Other',
    brand,
  }: BuildSeriesOptions = {},
): SeriesSet {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const n = name(row) ?? '';
    totals.set(n, (totals.get(n) ?? 0) + value(row));
  }

  const ranked = [...totals.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  const cap = Math.min(max, SERIES_COLORS.length);
  const keep = ranked.length > cap ? ranked.slice(0, cap - 1) : ranked;
  const folded = ranked.length > keep.length;

  // Slot order follows rank: the largest series gets slot 1 (the brand's
  // primary) and stack neighbours get the palette's adjacent slots, which are
  // the pairs it keeps distinguishable.
  const branded = brand
    ? brandPalette(brand.primary, brand.secondary, keep.length)
    : [];
  const colors = branded.length === keep.length ? branded : SERIES_COLORS;
  const byName = new Map<string, Series>(
    keep.map(([n], i) => [
      n,
      { key: `s${i}`, name: n || '–', color: colors[i] },
    ]),
  );
  const other: Series = {
    key: OTHER_KEY,
    name: otherLabel,
    color: OTHER_COLOR,
  };

  const series = keep.map(([n]) => byName.get(n) as Series);
  if (folded) series.push(other);

  const get = (n: string) => byName.get(n) ?? other;
  return {
    series,
    get,
    keyOf: (n) => get(n).key,
    config: Object.fromEntries(
      series.map((s) => [s.key, { label: s.name, color: s.color }]),
    ),
  };
}

/**
 * Narrows a series set to its `max` largest series, folding the rest into
 * "Other". Keeps keys and colours, so a donut can show fewer slices while
 * matching the other charts on the page.
 */
export function foldSeries(
  base: SeriesSet,
  max: number,
  otherLabel = 'Other',
): SeriesSet {
  const named = base.series.filter((s) => s.key !== OTHER_KEY);
  if (named.length <= max && named.length === base.series.length) return base;
  const keep = named.slice(0, Math.max(max - 1, 1));
  const kept = new Set(keep.map((s) => s.key));
  const other: Series = {
    key: OTHER_KEY,
    name: base.series.find((s) => s.key === OTHER_KEY)?.name ?? otherLabel,
    color: OTHER_COLOR,
  };
  const series = [...keep, other];
  const get = (n: string) => {
    const s = base.get(n);
    return kept.has(s.key) ? s : other;
  };
  return {
    series,
    get,
    keyOf: (n) => get(n).key,
    config: Object.fromEntries(
      series.map((s) => [s.key, { label: s.name, color: s.color }]),
    ),
  };
}

/** Sums `value` per group key. */
export function sumBy<R>(
  rows: readonly R[],
  key: (row: R) => string,
  value: (row: R) => number,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of rows) {
    const k = key(row);
    out.set(k, (out.get(k) ?? 0) + value(row));
  }
  return out;
}

/** A pivoted chart row: one numeric column per series key, all filled. */
export type PivotRow = {
  category: string;
  total: number;
  [seriesKey: string]: number | string;
};

/**
 * Pivots long rows into one row per category with a column per series.
 * Every series column is present (zero when missing) so stacks never have
 * holes. `categories` fixes the row set and order (e.g. every time bucket);
 * otherwise rows are sorted by total, largest first.
 */
export function pivot<R>(
  rows: readonly R[],
  {
    category,
    series,
    value,
    keys,
    categories,
  }: {
    category: (row: R) => string;
    series: (row: R) => string;
    value: (row: R) => number;
    keys: readonly string[];
    categories?: readonly string[];
  },
): PivotRow[] {
  const blank = (c: string): PivotRow => {
    const row: PivotRow = { category: c, total: 0 };
    for (const k of keys) row[k] = 0;
    return row;
  };

  const out = new Map<string, PivotRow>(
    (categories ?? []).map((c) => [c, blank(c)]),
  );
  for (const r of rows) {
    const c = category(r);
    const k = series(r);
    const v = value(r);
    let row = out.get(c);
    if (!row) {
      row = blank(c);
      out.set(c, row);
    }
    row[k] = ((row[k] as number | undefined) ?? 0) + v;
    row.total += v;
  }

  const result = [...out.values()];
  if (categories) {
    const order = new Map(categories.map((c, i) => [c, i]));
    return result.sort(
      (a, b) =>
        (order.get(a.category) ?? Number.MAX_SAFE_INTEGER) -
          (order.get(b.category) ?? Number.MAX_SAFE_INTEGER) ||
        a.category.localeCompare(b.category),
    );
  }
  return result.sort(
    (a, b) => b.total - a.total || a.category.localeCompare(b.category),
  );
}
